/**
 * Biometric Authentication Hook
 *
 * React hook for managing biometric authentication state and actions.
 * Handles Face ID, Touch ID, and fingerprint authentication with
 * secure credential storage.
 *
 * @module useBiometric
 */

import { useState, useEffect, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { BiometryType } from '@aparajita/capacitor-biometric-auth';
import {
  biometricService,
  BiometricAvailability,
  getBiometryTypeName,
} from '../services/biometric.service';

/**
 * Biometric hook state
 */
interface BiometricState {
  // Availability
  isAvailable: boolean;
  biometryType: BiometryType;
  biometryTypeName: string;
  isNativePlatform: boolean;

  // Stored credentials
  hasCredentials: boolean;

  // Loading states
  isCheckingAvailability: boolean;
  isAuthenticating: boolean;
  isSavingCredentials: boolean;

  // Error state
  error: string | null;
}

/**
 * Biometric hook return type
 */
interface UseBiometricReturn extends BiometricState {
  // Actions
  checkAvailability: () => Promise<BiometricAvailability>;
  checkStoredCredentials: () => Promise<boolean>;
  authenticate: () => Promise<{ success: boolean; email?: string; password?: string; error?: string }>;
  saveCredentials: (email: string, password: string) => Promise<boolean>;
  deleteCredentials: () => Promise<boolean>;
  clearError: () => void;
}

/**
 * Initial state
 */
const initialState: BiometricState = {
  isAvailable: false,
  biometryType: BiometryType.none,
  biometryTypeName: 'Biometric',
  isNativePlatform: false,
  hasCredentials: false,
  isCheckingAvailability: true,
  isAuthenticating: false,
  isSavingCredentials: false,
  error: null,
};

/**
 * Hook for biometric authentication
 */
export function useBiometric(): UseBiometricReturn {
  const [state, setState] = useState<BiometricState>(initialState);

  /**
   * Check if running on native platform (iOS/Android)
   */
  const isNativePlatform = Capacitor.isNativePlatform();
  console.log('[useBiometric] isNativePlatform:', isNativePlatform, 'platform:', Capacitor.getPlatform());

  /**
   * Check biometric availability
   */
  const checkAvailability = useCallback(async (): Promise<BiometricAvailability> => {
    if (!isNativePlatform) {
      setState(prev => ({
        ...prev,
        isAvailable: false,
        isNativePlatform: false,
        isCheckingAvailability: false,
      }));
      return {
        isAvailable: false,
        biometryType: BiometryType.none,
        errorMessage: 'Biometric is only available on native platforms',
      };
    }

    setState(prev => ({ ...prev, isCheckingAvailability: true }));

    try {
      const result = await biometricService.checkAvailability();

      setState(prev => ({
        ...prev,
        isAvailable: result.isAvailable,
        biometryType: result.biometryType,
        biometryTypeName: getBiometryTypeName(result.biometryType),
        isNativePlatform: true,
        isCheckingAvailability: false,
        error: result.isAvailable ? null : (result.errorMessage || null),
      }));

      return result;
    } catch (error) {
      setState(prev => ({
        ...prev,
        isAvailable: false,
        isCheckingAvailability: false,
        error: error instanceof Error ? error.message : 'Failed to check biometric availability',
      }));

      return {
        isAvailable: false,
        biometryType: BiometryType.none,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  }, [isNativePlatform]);

  /**
   * Check if credentials are stored
   */
  const checkStoredCredentials = useCallback(async (): Promise<boolean> => {
    if (!isNativePlatform) {
      return false;
    }

    try {
      const hasCredentials = await biometricService.hasStoredCredentials();
      setState(prev => ({ ...prev, hasCredentials }));
      return hasCredentials;
    } catch (error) {
      console.error('Failed to check stored credentials:', error);
      return false;
    }
  }, [isNativePlatform]);

  /**
   * Authenticate using biometric and return stored credentials
   */
  const authenticate = useCallback(async (): Promise<{
    success: boolean;
    email?: string;
    password?: string;
    error?: string;
  }> => {
    if (!isNativePlatform || !state.isAvailable) {
      return { success: false, error: 'Biometric not available' };
    }

    setState(prev => ({ ...prev, isAuthenticating: true, error: null }));

    try {
      const result = await biometricService.biometricLogin();

      setState(prev => ({ ...prev, isAuthenticating: false }));

      if (result.success && result.credentials) {
        return {
          success: true,
          email: result.credentials.username,
          password: result.credentials.password,
        };
      }

      setState(prev => ({ ...prev, error: result.error || 'Authentication failed' }));
      return { success: false, error: result.error || 'Authentication failed' };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Authentication failed';
      setState(prev => ({
        ...prev,
        isAuthenticating: false,
        error: errorMessage,
      }));
      return { success: false, error: errorMessage };
    }
  }, [isNativePlatform, state.isAvailable]);

  /**
   * Save credentials for biometric login
   */
  const saveCredentials = useCallback(async (
    email: string,
    password: string
  ): Promise<boolean> => {
    if (!isNativePlatform || !state.isAvailable) {
      return false;
    }

    setState(prev => ({ ...prev, isSavingCredentials: true, error: null }));

    try {
      const result = await biometricService.saveCredentials(email, password);

      setState(prev => ({
        ...prev,
        isSavingCredentials: false,
        hasCredentials: result.success,
        error: result.success ? null : (result.error || 'Failed to save credentials'),
      }));

      return result.success;
    } catch (error) {
      setState(prev => ({
        ...prev,
        isSavingCredentials: false,
        error: error instanceof Error ? error.message : 'Failed to save credentials',
      }));
      return false;
    }
  }, [isNativePlatform, state.isAvailable]);

  /**
   * Delete stored credentials
   */
  const deleteCredentials = useCallback(async (): Promise<boolean> => {
    if (!isNativePlatform) {
      return false;
    }

    try {
      const result = await biometricService.deleteCredentials();

      if (result.success) {
        setState(prev => ({ ...prev, hasCredentials: false }));
      }

      return result.success;
    } catch (error) {
      console.error('Failed to delete credentials:', error);
      return false;
    }
  }, [isNativePlatform]);

  /**
   * Clear error state
   */
  const clearError = useCallback(() => {
    setState(prev => ({ ...prev, error: null }));
  }, []);

  /**
   * Initialize on mount
   */
  useEffect(() => {
    let mounted = true;

    const initialize = async () => {
      console.log('[useBiometric] Initializing, isNativePlatform:', isNativePlatform);
      if (!isNativePlatform) {
        console.log('[useBiometric] Not native platform, skipping biometric init');
        if (mounted) {
          setState(prev => ({
            ...prev,
            isNativePlatform: false,
            isCheckingAvailability: false,
          }));
        }
        return;
      }

      // Check biometric availability
      console.log('[useBiometric] Checking biometric availability...');
      const availResult = await checkAvailability();
      console.log('[useBiometric] Availability result:', availResult);

      // Check stored credentials
      if (mounted) {
        await checkStoredCredentials();
      }
    };

    initialize();

    return () => {
      mounted = false;
    };
  }, [isNativePlatform, checkAvailability, checkStoredCredentials]);

  return {
    ...state,
    isNativePlatform,
    checkAvailability,
    checkStoredCredentials,
    authenticate,
    saveCredentials,
    deleteCredentials,
    clearError,
  };
}

export default useBiometric;
